import React from 'react'
import { Drawer, Button, Space, Divider, theme } from 'antd'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHeartPulse, faChartLine, faVial, faProcedures, faInfoCircle } from '@fortawesome/free-solid-svg-icons'

/**
 * FilterPanel Component
 * A slide-out drawer that contains section visibility toggles
 */
const FilterPanel = ({ visible, onClose, visibleSections, toggleSectionVisibility }) => {
  const { token } = theme.useToken()

  const buttonStyles = {
    sectionToggle: {
      width: '100%',
      marginBottom: 12,
      borderRadius: 8,
      height: 42,
      boxShadow: 'none',
      transition: 'all 0.3s ease',
      fontWeight: 500,
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start'
    },
    sectionToggleActive: {
      background: '#4A6CF7',
      color: 'white',
      boxShadow: '0 2px 8px rgba(74, 108, 247, 0.25)'
    },
    sectionToggleInactive: {
      background: '#F0F5FF',
      color: '#4A6CF7'
    },
    infoIcon: {
      marginLeft: 8,
      fontSize: 14,
      color: token.colorTextSecondary,
      cursor: 'help'
    }
  }

  return (
    <Drawer
      title='Assessment Sections'
      placement='right'
      width={300}
      onClose={onClose}
      open={visible}
      closable={true}
      bodyStyle={{ padding: '16px' }}
      headerStyle={{
        borderBottom: '1px solid #f0f0f0',
        padding: '16px 24px'
      }}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '12px 16px',
            borderTop: '1px solid #f0f0f0'
          }}
        >
          <Button type='primary' onClick={onClose}>
            Apply
          </Button>
        </div>
      }
    >
      <div>
        <p style={{ marginBottom: 16, color: token.colorTextSecondary }}>
          Toggle which sections are visible in your assessment view.
          <FontAwesomeIcon icon={faInfoCircle} style={buttonStyles.infoIcon} />
        </p>

        <Divider style={{ margin: '16px 0' }} />

        <Space direction='vertical' style={{ width: '100%' }}>
          <Button
            onClick={() => toggleSectionVisibility('vitalSigns')}
            icon={<FontAwesomeIcon icon={faHeartPulse} style={{ marginRight: 12, fontSize: 16 }} />}
            style={{
              ...buttonStyles.sectionToggle,
              ...(visibleSections.vitalSigns ? buttonStyles.sectionToggleActive : buttonStyles.sectionToggleInactive)
            }}
          >
            Vital Signs
          </Button>

          <Button
            onClick={() => toggleSectionVisibility('generalStats')}
            icon={<FontAwesomeIcon icon={faChartLine} style={{ marginRight: 12, fontSize: 16 }} />}
            style={{
              ...buttonStyles.sectionToggle,
              ...(visibleSections.generalStats ? buttonStyles.sectionToggleActive : buttonStyles.sectionToggleInactive)
            }}
          >
            General Stats
          </Button>

          <Button
            onClick={() => toggleSectionVisibility('bloodTests')}
            icon={<FontAwesomeIcon icon={faVial} style={{ marginRight: 12, fontSize: 16 }} />}
            style={{
              ...buttonStyles.sectionToggle,
              ...(visibleSections.bloodTests ? buttonStyles.sectionToggleActive : buttonStyles.sectionToggleInactive)
            }}
          >
            Blood Tests
          </Button>

          <Button
            onClick={() => toggleSectionVisibility('procedures')}
            icon={<FontAwesomeIcon icon={faProcedures} style={{ marginRight: 12, fontSize: 16 }} />}
            style={{
              ...buttonStyles.sectionToggle,
              ...(visibleSections.procedures ? buttonStyles.sectionToggleActive : buttonStyles.sectionToggleInactive)
            }}
          >
            Procedures
          </Button>
        </Space>
      </div>
    </Drawer>
  )
}

FilterPanel.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  visibleSections: PropTypes.shape({
    vitalSigns: PropTypes.bool.isRequired,
    generalStats: PropTypes.bool.isRequired,
    bloodTests: PropTypes.bool.isRequired,
    procedures: PropTypes.bool.isRequired
  }).isRequired,
  toggleSectionVisibility: PropTypes.func.isRequired
}

export default FilterPanel
